import { CustomHeader } from "@/components/CustomHeader";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import AlertModal from "@/components/ui/AlertModal.web";
import { Button } from "@/components/ui/Button";
import VehiclePlateComponent from "@/components/ui/VehiclePlate";
import VehiclePlateInput from "@/components/ui/VehiclePlateInput";
import { COUNTRIES } from "@/constants/countries";
import { useAuth } from "@/context/AuthContext";
import { UserDocument } from "@/types/user";
import { VehiclePlate } from "@/types/vehicle-plate";
import {
  formatVehiclePlateToObj,
  formatVehiclePlateToStr,
} from "@/utils/formatVehiclePlate";
import { Ionicons } from "@expo/vector-icons";
import { useLocalSearchParams, useRouter } from "expo-router";
import Head from "expo-router/head";
import { getApp } from "firebase/app";
import { doc, getDoc, getFirestore, setDoc } from "firebase/firestore";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, StyleSheet, TouchableOpacity } from "react-native";

const firestore = getFirestore(getApp());

type FormData = {
  followedPlates: VehiclePlate[];
};

type PendingPlateCandidate = {
  plate: string;
  country: string;
} | null;

type ModalInfo = {
  title: string;
  message: string;
  primaryAction?: {
    text: string;
    onPress: () => void;
    destructive?: boolean;
  };
  secondaryAction?: {
    text: string;
    onPress: () => void;
    destructive?: boolean;
  };
};

const SettingsPlatesPage = () => {
  const { user } = useAuth();
  const router = useRouter();
  const [userData, setUserData] = useState<UserDocument | null>(null);
  const { t } = useTranslation();
  const params = useLocalSearchParams<{ plateTour?: string; step?: string }>();
  const isPlatesTourStep = params.plateTour === "1" && params.step === "plates";
  const [tourDismissed, setTourDismissed] = useState(false);
  const showTourOverlay = isPlatesTourStep && !tourDismissed;
  const vehiclePlateInputRef = useRef<{
    submit: () => void;
    getPendingPlateCandidate: () => PendingPlateCandidate;
    clearInput: () => void;
  } | null>(null);
  const [modalInfo, setModalInfo] = useState<ModalInfo | null>(null);
  const [hasPendingInput, setHasPendingInput] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [preferredCountry, setPreferredCountry] = useState<string | undefined>(
    undefined,
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<FormData>({
    defaultValues: {
      followedPlates: [],
    },
  });

  const hasUnsavedChanges = isDirty || hasPendingInput;

  useEffect(() => {
    if (!user) return;

    const fetchUserData = async () => {
      try {
        const userDocRef = doc(firestore, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const data = userDocSnap.data() as UserDocument;
          setUserData(data);
          const initialPlates = data.followedPlates
            ? data.followedPlates.map(formatVehiclePlateToObj)
            : [];
          reset({ followedPlates: initialPlates });
          setPreferredCountry(
            (prev) =>
              prev ?? initialPlates[0]?.country ?? data.country ?? undefined,
          );
        }
      } catch (error) {
        console.error("Error loading plate settings:", error);
        setModalInfo({
          title: t("ERROR"),
          message: t("ERROR_LOADING_SETTINGS"),
        });
      }
    };

    fetchUserData();
  }, [user, reset, t]);

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  const handleSave = async (
    data: FormData,
    pendingCandidate: PendingPlateCandidate,
  ) => {
    if (!user) return;

    const plates = Array.isArray(data.followedPlates)
      ? data.followedPlates
      : [];
    const mergedPlates = [...plates];

    if (
      pendingCandidate &&
      pendingCandidate.plate &&
      pendingCandidate.plate.trim() !== "" &&
      !mergedPlates.some(
        (plateItem) =>
          plateItem.plate === pendingCandidate.plate &&
          plateItem.country === pendingCandidate.country,
      )
    ) {
      mergedPlates.push(pendingCandidate);
    }

    const validPlates = mergedPlates.filter(
      (p) => p && p.plate && p.plate.trim() !== "",
    );
    const formattedFollowedPlates = validPlates.map(formatVehiclePlateToStr);

    setIsSaving(true);
    try {
      const userDocRef = doc(firestore, "users", user.uid);
      await setDoc(
        userDocRef,
        { followedPlates: formattedFollowedPlates },
        { merge: true },
      );

      if (isPlatesTourStep) {
        setTourDismissed(true);
        await setDoc(
          userDocRef,
          { "onboardingPrefs.plateTourCompleted": true },
          { merge: true },
        );
        router.replace("/(tabs)/settings");
        return;
      }

      reset({ followedPlates: validPlates });
      vehiclePlateInputRef.current?.clearInput?.();
      setHasPendingInput(false);
    } catch (error) {
      console.error("Error saving plate settings:", error);
      setModalInfo({
        title: t("ERROR"),
        message: t("ERROR_SAVING_SETTINGS"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePress = () => {
    if (isSaving) return;
    const pendingCandidate =
      vehiclePlateInputRef.current?.getPendingPlateCandidate?.() || null;
    if (hasPendingInput && !pendingCandidate && !isDirty) {
      return;
    }
    handleSubmit((data) => handleSave(data, pendingCandidate))();
  };

  const handleFinishTour = async () => {
    setTourDismissed(true);

    if (!user) {
      router.replace("/(tabs)/settings-plates");
      return;
    }

    try {
      await setDoc(
        doc(firestore, "users", user.uid),
        { "onboardingPrefs.plateTourCompleted": true },
        { merge: true },
      );
    } catch (error) {
      console.error("Error completing plate tour:", error);
    } finally {
      router.replace("/(tabs)/settings-plates");
    }
  };

  const navigateBack = useCallback(() => {
    router.replace("/(tabs)/settings");
  }, [router]);

  const handleBackPress = () => {
    if (!hasUnsavedChanges) {
      navigateBack();
      return;
    }
    setModalInfo({
      title: t("UNSAVED_CHANGES_TITLE"),
      message: t("UNSAVED_CHANGES_MESSAGE"),
      secondaryAction: {
        text: t("CANCEL"),
        onPress: () => setModalInfo(null),
      },
      primaryAction: {
        text: t("DISCARD"),
        destructive: true,
        onPress: () => {
          setModalInfo(null);
          reset();
          vehiclePlateInputRef.current?.clearInput?.();
          setHasPendingInput(false);
          navigateBack();
        },
      },
    });
  };

  const handlePendingChange = useCallback((hasPending: boolean) => {
    setHasPendingInput(hasPending);
  }, []);

  const saveDisabled = !hasUnsavedChanges && !showTourOverlay;

  return (
    <>
      <Head>
        <title>{t("PAGE_TITLE_SETTINGS_PLATES")}</title>
      </Head>
      <CustomHeader
        title={t("PLATES")}
        type="subtitle"
        variant="tab"
        showBackButton
        onBackPress={handleBackPress}
      />
      <ThemedView style={styles.container}>
        <ThemedView
          style={[
            styles.platesSection,
            showTourOverlay && styles.tourHighlight,
          ]}
        >
          <ThemedText style={styles.sectionTitle}>
            {t("FOLLOWED_PLATES")}
          </ThemedText>
          <Controller
            control={control}
            name="followedPlates"
            render={({ field: { onChange, value } }) => {
              const currentPlate = value && value.length > 0 ? value[0] : null;
              const isSavedPlate =
                !!currentPlate && !isDirty && !hasPendingInput;
              const handleLocalRemove = () => onChange([]);
              const confirmUnfollow = () => {
                onChange([]);
                handleSubmit((data) => handleSave(data, null))();
              };
              const handleUnfollow = () => {
                setModalInfo({
                  title: t("UNFOLLOW_PLATE_TITLE"),
                  message: t("ARE_YOU_SURE_YOU_WANT_TO_UNFOLLOW_PLATE"),
                  secondaryAction: {
                    text: t("CANCEL"),
                    onPress: () => setModalInfo(null),
                  },
                  primaryAction: {
                    text: t("UNFOLLOW"),
                    destructive: true,
                    onPress: () => {
                      setModalInfo(null);
                      confirmUnfollow();
                    },
                  },
                });
              };
              return (
                <ThemedView style={styles.inputRow}>
                  {currentPlate ? (
                    <VehiclePlateComponent
                      country={currentPlate.country}
                      plate={currentPlate.plate}
                      onDelete={isSavedPlate ? undefined : handleLocalRemove}
                    />
                  ) : (
                    <ThemedView style={styles.inputSlot}>
                      <VehiclePlateInput
                        ref={vehiclePlateInputRef as any}
                        plates={value}
                        onChange={onChange}
                        limit={1}
                        countries={COUNTRIES}
                        defaultCountry={
                          preferredCountry ?? userData?.country ?? undefined
                        }
                        renderList={false}
                        onPendingChange={handlePendingChange}
                        onCountryChange={setPreferredCountry}
                      />
                    </ThemedView>
                  )}
                  {isSavedPlate ? (
                    <TouchableOpacity
                      onPress={handleUnfollow}
                      style={styles.unfollowButton}
                      accessibilityLabel={t("DISCARD")}
                    >
                      <Ionicons name="close" size={16} color="white" />
                    </TouchableOpacity>
                  ) : isSaving ? (
                    <ThemedView style={styles.spinnerSlot}>
                      <ActivityIndicator color="#0a7ea4" />
                    </ThemedView>
                  ) : (
                    <Button
                      text={t("SAVE")}
                      onPress={handleSavePress}
                      disabled={saveDisabled}
                      style={[
                        styles.saveButton,
                        showTourOverlay && styles.tourSaveButton,
                      ]}
                    />
                  )}
                </ThemedView>
              );
            }}
          />
        </ThemedView>
        {showTourOverlay && (
          <>
            <ThemedView pointerEvents="none" style={styles.tourBackdrop} />
            <ThemedView style={styles.tourTooltip}>
              <ThemedText style={styles.tourTooltipTitle}>
                {t("PLATE_TOUR_STEP2_TITLE")}
              </ThemedText>
              <ThemedText style={styles.tourTooltipText}>
                {t("PLATE_TOUR_STEP2_MESSAGE")}
              </ThemedText>
              <TouchableOpacity onPress={handleFinishTour}>
                <ThemedText type="link">{t("GOT_IT")}</ThemedText>
              </TouchableOpacity>
            </ThemedView>
          </>
        )}
      </ThemedView>
      {modalInfo && (
        <AlertModal
          visible={!!modalInfo}
          onClose={() => setModalInfo(null)}
          title={modalInfo.title}
          message={modalInfo.message}
          primaryAction={modalInfo.primaryAction}
          secondaryAction={modalInfo.secondaryAction}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    alignSelf: "center",
    width: "100%",
    maxWidth: 600,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginTop: 10,
    marginBottom: 12,
  },
  inputRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  inputSlot: {
    flex: 1,
  },
  saveButton: {
    flexShrink: 0,
    width: 100,
  },
  spinnerSlot: {
    flexShrink: 0,
    width: 100,
    height: 44,
    alignItems: "flex-start",
    justifyContent: "center",
  },
  unfollowButton: {
    width: 24,
    height: 24,
    borderRadius: 4,
    backgroundColor: "red",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
    flexShrink: 0,
  },
  platesSection: {
    position: "relative",
  },
  tourHighlight: {
    borderWidth: 2,
    borderColor: "#0a7ea4",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingBottom: 12,
    backgroundColor: "#fff",
    zIndex: 3,
  },
  tourBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.45)",
    zIndex: 1,
  },
  tourTooltip: {
    position: "absolute",
    top: 180,
    right: 20,
    left: 20,
    borderRadius: 10,
    backgroundColor: "#fff",
    padding: 12,
    gap: 8,
    zIndex: 4,
    borderWidth: 1,
    borderColor: "#d9d9d9",
  },
  tourTooltipTitle: {
    fontSize: 15,
    fontWeight: "700",
  },
  tourTooltipText: {
    fontSize: 14,
  },
  tourSaveButton: {
    zIndex: 3,
  },
});

export default SettingsPlatesPage;
